import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"

const buttonVariants = cva("h-button", {
  variants: {
    variant: {
      default: "h-button--default",
      primary: "h-button--primary",
      danger: "h-button--danger",
      secondary: "h-button--secondary",
      ghost: "h-button--ghost",
      dangerLight: "h-button--danger-light",
      successLight: "h-button--success-light",
      invert: "h-button--invert",
      link: "h-button--link",
      linkGrey: "h-button--link-grey",
    },
    size: {
      sm: "h-button--sm",
      xs: "h-button--xs",
      xxs: "h-button--xxs",
      md: "h-button--md",
      lg: "h-button--lg",
    },
    iconOnly: {
      true: "h-button--icon",
    },
    fullWidth: {
      true: "h-button--block",
    },
  },
})

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      iconOnly,
      fullWidth,
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button"

    // Automatically pass size to Icon children
    // Default: always use "sm" for icons (based on the design system)
    // Skip this processing when asChild is true - let the child element handle its own children
    const processedChildren = asChild
      ? children
      : React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            const props = child.props as Record<string, unknown>
            if (props && typeof props === "object" && "icon" in props) {
              // If icon already has a size, respect it
              if (props.size) {
                return child
              }
              // Exception: iconOnly + lg button uses default icon size
              if (iconOnly && size === "lg") {
                return child
              }
              // Default: always use "sm" for icons
              return React.cloneElement(child, {
                size: "sm",
              } as React.HTMLAttributes<HTMLSpanElement>)
            }
          }
          return child
        })

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, iconOnly, fullWidth, className }),
        )}
        ref={ref}
        {...props}
      >
        {processedChildren}
      </Comp>
    )
  },
)
Button.displayName = "Button"

const ButtonSuffix = ({ children }: { children: React.ReactNode }) => {
  return <div className="h-button__suffix">{children}</div>
}
ButtonSuffix.displayName = "ButtonSuffix"

export { Button, ButtonSuffix, buttonVariants }
